import React, { useState } from 'react';
import { Alert, Popover, Tooltip } from 'antd';
import { Moment } from 'moment';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrosshairs } from '@fortawesome/pro-light-svg-icons';
import { TimeZoneFormat } from '@shared/utils/constants';
import Button from '@client/components/Button';
import { DatePickerValue } from '@client/components/DatePicker';
import {
	normalizePortDateTime,
	PortAwareDatePicker,
	toPortDateTimeInputValue,
} from './PortAwareDatePicker';
import styles from './SetEtdPopover.module.css';

type Props = {
	currentEtd: Moment | null | undefined;
	timezone: string;
	disabledReason: string | null;
	onSet: (targetEtd: Moment) => string | null;
};

const SetEtdPopover = ({
	currentEtd,
	timezone,
	disabledReason,
	onSet,
}: Props) => {
	const [open, setOpen] = useState(false);
	const [picked, setPicked] = useState<DatePickerValue>(null);
	const [error, setError] = useState<string | null>(null);

	const disabled = disabledReason != null;

	const onOpenChange = (nextOpen: boolean) => {
		if (disabled) {
			return;
		}

		setOpen(nextOpen);
		setError(null);

		if (nextOpen) {
			setPicked(toPortDateTimeInputValue(currentEtd, TimeZoneFormat.LocalTime, timezone));
		}
	};

	const onSubmit = () => {
		const targetEtd = normalizePortDateTime(picked, TimeZoneFormat.LocalTime, timezone);

		if (targetEtd == null) {
			return;
		}

		const nextError = onSet(targetEtd);

		if (nextError != null) {
			setError(nextError);

			return;
		}

		setOpen(false);
	};

	return (
		<Tooltip title={disabledReason ?? 'Set ETD'}>
			<span>
				<Popover
					trigger="click"
					title="Set ETD"
					open={!disabled && open}
					onOpenChange={onOpenChange}
					content={(
						<div className={styles.content}>
							<PortAwareDatePicker
								time
								timezone={timezone}
								timeZoneFormat={TimeZoneFormat.LocalTime}
								value={picked}
								onChange={(value) => {
									setPicked(value);
									setError(null);
								}}
							/>
							{error != null && (
								<Alert
									className={styles.alert}
									type="error"
									showIcon
									message={error}
								/>
							)}
							<Button
								type="primary"
								disabled={picked == null}
								onClick={onSubmit}
							>
								Set ETD
							</Button>
						</div>
					)}
				>
					<span
						data-testid="set-etd-button"
						className={classNames(styles.trigger, { [styles.disabled]: disabled })}
					>
						<FontAwesomeIcon
							color="#1677ff"
							icon={faCrosshairs}
						/>
					</span>
				</Popover>
			</span>
		</Tooltip>
	);
};

export default SetEtdPopover;
